<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <PartnerToolbar
        v-if="Object.keys(partner).length !== 0"
        :partner="partner"
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-bottom">
            <div class="head-card-top mb-2">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="flat-primary"
                :to="{ name: 'dashboard-partner-stats-dsp', params: { id: $route.params.id } }"
                pill
                exact
              >
                {{ $t('Overview') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-dsp-partners' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-dsp-partners', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('DSP Partners') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-geo' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-geo', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('Geo') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-device' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-device', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('Device') }}
              </b-button>
            </div>
          </div>
          <div class="head-card-top">
            <h4>{{ $t('DSP Partners Statistics') }}</h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              v-model="rangePicker"
              :is-loading="loading"
              @range-change="toggleRange"
              @refresh="refreshData"
            />
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <div v-else-if="processedDspPartners.length > 0">
        <b-table
          small
          :items="processedDspPartners"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          responsive
          striped
          foot-clone
        >
          <template #head(partner_id)>
            {{ $t('Partner') }}
          </template>

          <template #head(impressions)>
            {{ $t('Impressions') }}
          </template>

          <template #head(clicks)>
            {{ $t('Clicks') }}
          </template>

          <template #head(revenue)>
            {{ $t('Revenue') }}
          </template>

          <template #head(tax)>
            {{ $t('Tax') }}
          </template>

          <template #cell(partner_id)="row">
            <router-link
              v-if="row.item.partner_name"
              :to="{
                name: 'dashboard-partner-stats-dsp-partners-details',
                params: {
                  id: $route.params.id,
                  dspId: row.item.partner_id,
                },
              }"
              class="d-flex align-items-center text-decoration-none"
            >
              <strong>{{ row.item.partner_name }}</strong>
            </router-link>
          </template>

          <template #cell(impressions)="row">
            <span v-if="row.item.impressions">
              {{ formatNumber(row.item.impressions) }}
            </span>
          </template>

          <template #cell(clicks)="row">
            <span>
              {{ formatNumber(row.item.clicks) }}
            </span>
          </template>

          <template #cell(revenue)="row">
            <span>
              ${{ formatNumber(row.item.revenue, 2) }}
            </span>
          </template>

          <template #cell(tax)="row">
            <span>
              ${{ formatNumber(row.item.tax, 2) }}
            </span>
          </template>

          <template #foot(partner_id)>
            <strong>{{ $t('Records') }}: {{ processedDspPartners.length }}</strong>
          </template>

          <template #foot(impressions)>
            <strong>{{ formatNumber(totalStats.impressions) }}</strong>
          </template>

          <template #foot(clicks)>
            <strong>{{ formatNumber(totalStats.clicks) }}</strong>
          </template>

          <template #foot(revenue)>
            <strong>${{ formatNumber(totalStats.revenue, 2) }}</strong>
          </template>

          <template #foot(tax)>
            <strong>${{ formatNumber(totalStats.tax, 2) }}</strong>
          </template>
        </b-table>
      </div>
      <div
        v-else
        class="alert-body text-center"
      >
        <span>{{ $t('No data for this period') }}</span>
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import {
  BCard,
  BTable,
  BSpinner,
  BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import PartnerToolbar from '@/views/dashboard/partners/PartnerToolbar.vue'
import getFormattedDate from '@/utils/date-formatter'
import StatsNavigation from './StatsNavigation.vue'
import StatsDateRangeFilter from './StatsDateRangeFilter.vue'

export default {
  components: {
    Breadcrumbs,
    BCard,
    BTable,
    BSpinner,
    BButton,
    PartnerToolbar,
    StatsNavigation,
    StatsDateRangeFilter,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: true,
      partner: {},
      dspPartners: [],
      dspPartnersList: [], // список всех DSP партнеров для конвертации ID в имена
      breadcrumbItems: [],
      rangePicker: [],
      rangeDefault: [getFormattedDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), getFormattedDate(new Date())],
      sortBy: 'revenue',
      sortDesc: true,
      sortDirection: 'desc',
      fields: [
        { key: 'partner_id', label: 'Partner ID', sortable: true },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'clicks', label: 'Clicks', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
        { key: 'tax', label: 'Tax', sortable: true },
      ],
    }
  },
  computed: {
    processedDspPartners() {
      return this.dspPartners.map(item => ({
        ...item,
        partner_name: this.getPartnerNameById(item.partner_id),
      }))
    },
    getPartnerNameById() {
      return partnerId => {
        const partner = this.dspPartnersList.find(p => p._id === partnerId)
        return partner ? partner.name : partnerId
      }
    },
    totalStats() {
      if (this.dspPartners.length === 0) {
        return {
          impressions: 0,
          clicks: 0,
          revenue: 0,
          tax: 0,
        }
      }

      const totals = this.dspPartners.reduce(
        (acc, item) => {
          acc.impressions += item.impressions || 0
          acc.clicks += item.clicks || 0
          acc.revenue += item.revenue || 0
          acc.tax += item.tax || 0
          return acc
        },
        {
          impressions: 0,
          clicks: 0,
          revenue: 0,
          tax: 0,
        },
      )

      return totals
    },
  },
  async created() {
    this.rangePicker = this.rangeDefault
    await this.getPartnerData()
    await this.getDspPartnersList()
    await this.getDspPartnersStats()
  },
  methods: {
    setBreadcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('Partners'),
          to: '/partners/',
        },
        {
          text: this.partner.name,
          to: `/partners/${this.partner._id}/`,
        },
        {
          text: this.$t('Statistics'),
          to: `/partners/${this.partner._id}/stats`,
        },
        {
          text: this.$t('DSP Partners'),
          active: true,
        },
      ]
    },
    async getPartnerData() {
      try {
        const responseData = await useJwt.getPartner(this.$route.params.id)
        this.partner = responseData.data.partner || {}
        this.setBreadcrumbs()
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async getDspPartnersList() {
      try {
        const responseData = await useJwt.dsppartners()
        this.dspPartnersList = responseData.data.dsppartners || []
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async getDspPartnersStats() {
      this.loading = true
      try {
        let dateFrom
        let dateTo

        const selectedRange = this.rangePicker
        if (Array.isArray(selectedRange)) {
          [dateFrom, dateTo] = selectedRange
        } else {
          [dateFrom, , dateTo] = selectedRange.split(' ')
        }

        if (!dateTo && !dateFrom) {
          [dateFrom, dateTo] = this.rangeDefault
        }

        if (dateTo && dateFrom) {
          const params = {
            from: dateFrom,
            to: dateTo,
            openrtb_partner_id: this.$route.params.id,
            type: 'daily',
          }

          const responseData = await useJwt.getDsppartnerStatsTotal(params)
          this.dspPartners = responseData.data.items || []
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
        this.dspPartners = []
      } finally {
        this.loading = false
      }
    },
    async toggleRange() {
      await this.getDspPartnersStats()
    },
    async refreshData() {
      await this.getDspPartnersStats()
    },
    formatNumber(value, decimals = 0) {
      if (value === null || value === undefined) return '-'
      return new Intl.NumberFormat('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value)
    },
    getFormattedDate,
  },
}
</script>

<style lang="scss" scoped>
.card-statistics {
  .head-card {
    .head-card-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;

      h4 {
        margin: 0;
        font-weight: 600;
      }
    }

    .head-card-bottom {
      .head-card-top {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
      }
    }
  }
}

@media (max-width: 768px) {
  .card-statistics {
    .head-card {
      .head-card-top {
        flex-direction: column;
        align-items: flex-start;
      }

      .head-card-bottom {
        .head-card-top {
          justify-content: flex-start;
        }
      }
    }
  }
}
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style>
  .col-picker {
    min-width: 220px;
  }
</style>
